import React, { Fragment, useEffect, useState, useRef } from "react";
import DOMPurify from "dompurify";
import { useMediaQuery } from "react-responsive";
import { useCart } from "react-use-cart";
import {
  Box,
  Backdrop,
  Button,
  ButtonGroup,
  CircularProgress,
  Container,
  Grid,
  IconButton,
  ImageList,
  ImageListItem,
  Link,
  Paper,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tabs,
  TextField,
  Typography,
} from "@mui/material";
import { useNavigate } from "react-router-dom";
import { Navigation } from "swiper/modules";
import "swiper/css";
import "swiper/css/navigation";
import { AccountType, ProductAvailability } from "../../utils/constants";
import { SouthAfricanRand } from "../../utils/helpers";
import "./style.css";
import { useArgusCart } from "../../contexts/ArgusCartContext";
import { Swiper, SwiperSlide } from "swiper/react";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import ShoppingCartIcon from "@mui/icons-material/ShoppingCart";
import SubdirectoryArrowLeftIcon from "@mui/icons-material/SubdirectoryArrowLeft";
import RemoveIcon from "@mui/icons-material/Remove";
import AddIcon from "@mui/icons-material/Add";
import WarningIcon from "@mui/icons-material/Warning"; // For Low Stock
import DoNotDisturbOnIcon from "@mui/icons-material/DoNotDisturbOn"; // For No Stock
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import { cloneDeep } from "lodash";
import TabPanel from "../TabPanel/TabPanel";
import { COLORS } from "../../constants";
import GridSpecials from "../GridSpecials/GridSpecials";
import RelatedProducts from "../RelatedProducts/RelatedProducts";
import YouMightAlsoLike from "../YouMightAlsoLike/YouMightAlsoLike";
import DesktopProduct from "./DesktopProduct";
import MobileProduct from "./MobileProduct";
import useSystemHook from "./../../hooks/SystemHook";

const Product = ({
  product,
  pricingData,
  stockInfo,
  applicationFilters,
  applicationMakes,
  applicationModels,
  applicationVariants,
  applicationYears,
  setApplicationFilters,
  accountType,
  loading,
  applicationTabIsLoading,
  authError,
}) => {
  const {
    handleAddToCart,
    totalItems,
    items,
    setItems,
    removeItem,
    emptyCart,
    clearCartMetadata,
    updateItemQuantity,
  } = useArgusCart();
  const navigate = useNavigate();
  const [tabValue, setTabValue] = useState("description");
  const [productPrice, setProductPrice] = useState(null);
  const [availabilityValue, setAvailabilityValue] = useState(null);
  const [colourForAvailability, setColourForAvailability] = useState(null);
  const [textForAvailability, setTextForAvailability] = useState(null);
  const [colourForQuality, setColourForQuality] = useState(null);
  const [textForQuality, setTextForQuality] = useState(null);
  const [quantity, setQuantity] = useState(1);
  const swiperRef = useRef(null);
  const [products, setProducts] = useState(null);

  const { addItem } = useCart();

  const applicationColumns = [
    { key: "make", data: applicationMakes, filterType: "makeId" },
    { key: "model", data: applicationModels, filterType: "modelId" },
    { key: "variant", data: applicationVariants, filterType: "variantId" },
    { key: "year", data: applicationYears, filterType: null }, // No click handler needed for years
  ];

  const onBackClick = () => {
    return navigate(-1);
  };

  const sanitizedData = () => ({
    __html: !!product ? DOMPurify.sanitize(product.html) : null,
  });

  const handleTabChange = (event, newValue) => {
    setTabValue(newValue);
  };

  const handleApplicationFilterClick = (type, value) => {
    const filterCopy = cloneDeep(applicationFilters);
      filterCopy[type] = value?.makeId ?? value?.modelId ?? value?.variantId;

    if (type === "makeId") {
      filterCopy.modelId = null;
      filterCopy.variantId = null;
    } else if (type === "modelId") {
      filterCopy.variantId = null;
    }

    setApplicationFilters(filterCopy);
  };

  const handleAddToCartClick = () => {
    handleAddToCart({
      id: product.productId,
      name: product.name,
      vendor: product.vendor,
      image: product.image,
      sku: product.sku,
      quantity: quantity,
    });
  };

  const { isMobile, isTablet, isDesktop } = useSystemHook();

  useEffect(() => {
    if (!!pricingData && pricingData.length > 0) {
      setProductPrice(pricingData[0]);
    }
  }, [pricingData]);

  useEffect(() => {
    if (!!product) {
      const tempAvailabilityValue =
        !!stockInfo && stockInfo.length > 0
          ? stockInfo[0].quantityOnHand
          : product.availability;
      setAvailabilityValue(tempAvailabilityValue);
      switch (tempAvailabilityValue) {
        case ProductAvailability.InStock:
          setColourForAvailability("green");
          setTextForAvailability(
            "This item is in stock at our warehouse. See our delivery policy to see when you can expect it.",
          );
          break;
        case ProductAvailability.LowStock:
          setColourForAvailability("blue");
          setTextForAvailability(
            "This item shows as having low stock in our warehouse. Please be aware that in some cases, this item may already be sold out and we may have to order this item in or refund your order.",
          );
          break;
        case ProductAvailability.ThreeToFiveDays:
          setColourForAvailability("orange");
          setTextForAvailability(
            "This item is in-stock at our suppliers. When you place an order for this item, we will collect it from our supplier and deliver it to you. Please see our delivery policy to see when you can expect it.",
          );
          break;
        case ProductAvailability.No_Stock:
          setColourForAvailability("red");
          setTextForAvailability(
            "This item is unfortunately out of stock, but more stock is on the way! Please contact us or regularly check our website to be notified when this item is back in stock.",
          );
          break;
        default:
          setColourForAvailability("");
          setTextForAvailability("");
          break;
      }
      switch (product.quality) {
        case "A+":
          setColourForQuality("violet");
          setTextForQuality("Manufacturer is a recognised O.E. supplier.");
          break;
        case "A":
          setColourForQuality("blue");
          setTextForQuality(
            "Aftermarket part manufactured to O.E. equivalent specifications.",
          );
          break;
        case "B":
          setColourForQuality("teal");
          setTextForQuality(
            "Economical replacement aftermarket part conforms to regulator requirements.",
          );
          break;
        case "Other":
          setColourForQuality("olive");
          setTextForQuality(
            "Applies to tools, accessories and non car specific parts.",
          );
          break;
        default:
          setColourForQuality("");
          setTextForQuality("");
          break;
      }
    }
  }, [product, stockInfo]);

  const numberOfRows = Math.max(
    applicationMakes.length,
    applicationModels.length,
    applicationVariants.length,
    applicationYears.length,
  );

  return (
    <Fragment>
      {isMobile === true && (
        <MobileProduct
          product={product}
          pricingData={pricingData}
          accountType={accountType}
          loading={loading}
          handleAddToCartClick={handleAddToCartClick}
          productPrice={productPrice}
          availabilityValue={availabilityValue}
          tabValue={tabValue}
          handleTabChange={handleTabChange}
          quantity={quantity}
          setQuantity={setQuantity}
          handleApplicationFilterClick={handleApplicationFilterClick}
          numberOfRows={numberOfRows}
          applicationColumns={applicationColumns}
          applicationMakes={applicationMakes}
          applicationModels={applicationModels}
          applicationVariants={applicationVariants}
          applicationYears={applicationYears}
          applicationFilters={applicationFilters}
          setApplicationFilters={setApplicationFilters}
          authError={authError}
        />
      )}

      {isMobile === false && (
        <DesktopProduct
          product={product}
          pricingData={pricingData}
          accountType={accountType}
          loading={loading}
          handleAddToCartClick={handleAddToCartClick}
          productPrice={productPrice}
          availabilityValue={availabilityValue}
          tabValue={tabValue}
          handleTabChange={handleTabChange}
          quantity={quantity}
          setQuantity={setQuantity}
          handleApplicationFilterClick={handleApplicationFilterClick}
          numberOfRows={numberOfRows}
          applicationColumns={applicationColumns}
          applicationMakes={applicationMakes}
          applicationModels={applicationModels}
          applicationVariants={applicationVariants}
          applicationYears={applicationYears}
          applicationFilters={applicationFilters}
          setApplicationFilters={setApplicationFilters}
          authError={authError}
        />
      )}
    </Fragment>
  );
};

export default Product;
